import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Twitter, Music2, Mail } from "lucide-react";
import api from "@/lib/api";

const NAV = [
  { to: "/shop", label: "Shop" },
  { to: "/pillars", label: "The Pillars" },
  { to: "/story", label: "Our Story" },
  { to: "/gallery", label: "Gallery" },
  { to: "/find-your-pillar", label: "Find Your Pillar" },
  { to: "/free-wallpapers", label: "Free Wallpapers" },
  { to: "/join", label: "Join" },
  { to: "/contact", label: "Contact" },
];

export default function Footer() {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const socials = [
    settings?.facebook_url && { href: settings.facebook_url, label: "Facebook", Icon: Facebook },
    settings?.instagram_url && { href: settings.instagram_url, label: "Instagram", Icon: Instagram },
    settings?.twitter_url && { href: settings.twitter_url, label: "Twitter", Icon: Twitter },
    settings?.tiktok_url && { href: settings.tiktok_url, label: "TikTok", Icon: Music2 },
    settings?.contact_email && { href: `mailto:${settings.contact_email}`, label: "Email", Icon: Mail },
  ].filter(Boolean);

  return (
    <footer data-testid="site-footer" className="relative overflow-hidden border-t border-white/10 bg-[#0A0A0B] px-6 pb-10 pt-24 md:px-12 md:pt-32">
      <div className="pointer-events-none select-none" aria-hidden="true">
        <p className="whitespace-nowrap font-serif text-[11vw] font-light leading-none tracking-tighter text-white/[0.06]">
          THREADS UNBOUND™
        </p>
      </div>
      <div className="mt-16 grid gap-12 md:grid-cols-3">
        <div>
          <p className="font-serif text-xl font-light text-white/80">
            Art worn like a <span className="italic text-[#D4AF37]">manifesto</span>.
          </p>
          <p className="mt-4 max-w-xs text-sm leading-relaxed text-white/40">
            An art and apparel house built on six pillars: Freedom, Chaos, Truth, Grit, Rebellion, and Wisdom.
          </p>
        </div>
        <nav aria-label="Footer">
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/30">Navigate</p>
          <ul className="mt-4 space-y-2">
            {NAV.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  data-testid={`footer-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="text-sm text-white/60 transition-colors duration-300 hover:text-[#D4AF37]"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/30">Connect</p>
          <div className="mt-4 flex gap-4">
            {socials.map(({ href, label, Icon }) => (
              <a
                key={label}
                href={href}
                target={href.startsWith("http") ? "_blank" : undefined}
                rel="noreferrer"
                aria-label={label}
                data-testid={`footer-social-${label.toLowerCase()}`}
                className="border border-white/15 p-2.5 text-white/60 transition-colors duration-300 hover:border-[#D4AF37] hover:text-[#D4AF37]"
              >
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
          <p className="mt-6 text-xs text-white/30">Designed in the dark. Worn in the light.</p>
        </div>
      </div>
      <div className="mt-16 flex flex-col gap-2 border-t border-white/10 pt-6 text-xs text-white/30 md:flex-row md:items-center md:justify-between">
        <span>© {new Date().getFullYear()} Threads Unbound™. All rights reserved.</span>
        <span>Freedom · Chaos · Truth · Grit · Rebellion · Wisdom</span>
      </div>
    </footer>
  );
}
